import React, { useState } from 'react'
import { currency } from 'lib/utils/string'
import { useMutation } from '@apollo/client'
import { useSnackbar } from 'notistack'
import { useForm, FormProvider } from 'react-hook-form'
import { validationSchema } from '../utils'
import { yupResolver } from '@hookform/resolvers'
import {
  useStripe,
  useElements,
  CardNumberElement,
} from '@stripe/react-stripe-js'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import Button from '@material-ui/core/Button'
import { useAuth } from 'lib/auth'
import { withStripeElements, calculateStripeFee } from 'lib/stripe'
import StepDescriptionOfferings from '../step-description-offerings'
import { CREATE_LESSON_FOR_STUDENT_QUERY } from 'pages/schedule/mutations'
import PaymentInformation from './payment-information'
import { useStyles } from './styles'
import { gtmEvent } from 'lib/gtm'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import BackButton from '../components/BackButton'
import { IconInfoActive } from 'components/atoms/icons'
import themes, { extendTheme } from 'themes'
import { ThemeProvider } from '@material-ui/core'
import BookingSummaryCoachInfo from '../components/BookingSummaryCoachInfo'
import InitialsAvatar from 'components/avatars/initials-avatar'
import PropTypes from 'prop-types'
import BookingConfirmationSkeleton from 'components/skeletons/booking-confirmation-skeleton'
import ProgressIndicator from 'components/progress-indicators/progress-indicator'

const LessonReviewLayout = ({
  lessonType,
  locationId,
  startDateTime,
  student,
  previousUrl,
  isComplete,
  setIsComplete,
  setLastDigits,
  setCardBrand,
  coachIsViewingOwnSchedule,
}) => {
  const { priceInCents, coach, locations, acceptsOnlinePayments } = lessonType
  const facility = locations.find(({ id }) => id === locationId)

  const studentName = `${student.firstName} ${student.lastName}`
  const [isStripeFlatFeeDeducted] = useFeatureFlags([
    flags.FLAT_FEAT_STRIPE_FEE_DEDUCTED,
  ])
  const priceInDollars = priceInCents ? priceInCents / 100 : null
  const processingFeeInDollars = isStripeFlatFeeDeducted
    ? 0
    : calculateStripeFee(priceInCents || 0) / 100

  const { isCoach } = useAuth()
  const [paymentRequired, setPaymentRequired] = useState(
    !isCoach && acceptsOnlinePayments,
  )

  const { enqueueSnackbar } = useSnackbar()
  const [createLesson, { loading }] = useMutation(
    CREATE_LESSON_FOR_STUDENT_QUERY,
  )

  const form = useForm({
    resolver: yupResolver(validationSchema(paymentRequired)),
  })

  const stripe = useStripe()
  const elements = useElements()
  const classes = useStyles()

  const defaultRedirectUrl = `${window.location.origin}${
    isCoach ? '/pga-coach/bookings/schedule' : '/club'
  }`

  const onSubmit = (redirectUrl = defaultRedirectUrl) => async formValues => {
    const { error: stripeError, token: stripeToken } = await (paymentRequired
      ? stripe.createToken(elements.getElement(CardNumberElement), {
        name: formValues.payment.name,
        address_country: 'US',
      })
      : Promise.resolve({ error: null, token: null }))
    if (stripeError) {
      enqueueSnackbar(stripeError.message, {
        variant: 'error',
      })
      return Promise.resolve()
    }

    return createLesson({
      variables: {
        input: {
          studentId: student.id,
          lessonTypeId: lessonType.id,
          startDateTime,
          locationId: facility.id,
          stripeToken: stripeToken?.id,
        },
      },
    })
      .then(({ data }) => {
        const { success, message } = data.createLessonForStudent

        if (success) {
          enqueueSnackbar('Your session has been booked.', {
            variant: 'success',
          })
          gtmEvent({
            event: 'formSubmit',
            formCategory: 'book-session',
            formAction: 'clicked-book-session-only-button',
          })

          setIsComplete(!isComplete)
          setLastDigits(stripeToken?.card.last4)
          setCardBrand(stripeToken?.card.brand)
        } else {
          enqueueSnackbar(message, {
            variant: 'error',
          })
        }
      })
      .catch(error => {
        window.rg4js &&
          window.rg4js('send', {
            error: error,
            tags: ['bookings', 'form-submit'],
          })
        enqueueSnackbar('An error has occurred. Please try again later.', {
          variant: 'error',
        })
      })
  }

  if (loading) {
    return (
      <Container className={classes.root}>
        <div className={classes.loadingStyles}>
          <BookingConfirmationSkeleton />
        </div>
      </Container>
    )
  }

  return (
    <Container className={classes.rootRefresh}>
      <ThemeProvider theme={extendTheme(themes.offeringsRefresh)}>
        <Grid container>
          <ProgressIndicator value={75} />
          <BackButton previousUrl={previousUrl} />
          <BookingSummaryCoachInfo
            lessonType={lessonType}
            coach={coach}
            classes={classes}
          />
          <Grid item xs={12}>
            <Divider className={classes.divider} />
          </Grid>
          <Grid container>
            <Grid item md={7} xs={12} className={classes.gridItem}>
              <StepDescriptionOfferings
                stepText="Step 3 of 3"
                title="Review"
                description="Please review the information below."
              />
              <Grid
                container
                item
                xs={12}
                md={5}
                className={classes.studentCard}
              >
                <InitialsAvatar
                  name={`${student.firstName} ${student.lastName}`}
                  classes={classes}
                />
                <div className={classes.infoWrapped}>
                  <Typography
                    variant="caption"
                    className={`${classes.fontBolded} ${classes.captionText}`}
                    data-testid="student-name"
                  >
                    {studentName}
                  </Typography>
                  <Typography
                    variant="caption"
                    className={`${classes.captionText} ${classes.lighterText}`}
                  >
                    Student
                  </Typography>
                </div>
              </Grid>
              <Divider style={{ margin: '2rem 0px 1rem' }} />
              {priceInDollars !== null && (
                <Box mb={2}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body1" className={classes.lighterText}>
                      Subtotal
                    </Typography>
                    <Typography variant="body1" className={classes.lighterText}>
                      {currency(priceInDollars)}
                    </Typography>
                  </Box>
                  {paymentRequired ? (
                    <>
                      {!isStripeFlatFeeDeducted && (
                        <Box
                          display="flex"
                          className={classes.infoRow}
                          justifyContent="space-between"
                          mb={1}
                        >
                          <Typography
                            variant="caption"
                            className={classes.captionText}
                          >
                            Processing Fee
                          </Typography>
                          <Typography
                            variant="caption"
                            className={classes.captionText}
                          >
                            {currency(processingFeeInDollars)}
                          </Typography>
                        </Box>
                      )}
                      <Box
                        display="flex"
                        className={classes.infoRow}
                        justifyContent="space-between"
                        mb={1}
                      >
                        <Typography
                          variant="body1"
                          className={`${classes.fontBolded}`}
                        >
                          Due Today
                        </Typography>
                        <Typography
                          variant="body1"
                          className={`${classes.fontBolded}`}
                        >
                          {currency(priceInDollars + processingFeeInDollars)}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box
                        display="flex"
                        className={classes.infoRow}
                        justifyContent="space-between"
                      >
                        <Typography
                          variant="body1"
                          className={`${classes.fontBolded}`}
                        >
                          Due Today
                        </Typography>
                        <Typography
                          variant="body1"
                          className={`${classes.fontBolded}`}
                        >
                          {currency(0)}
                        </Typography>
                      </Box>
                      <div className={classes.infoRow}>
                        <Typography
                          variant="body2"
                          className={`${classes.captionText} ${classes.lighterText}`}
                        >
                          Pay at location
                        </Typography>
                      </div>
                    </>
                  )}
                  <Divider style={{ margin: '1rem 0px 1rem' }} />
                </Box>
              )}
              {paymentRequired && (
                <Box>
                  <Grid
                    container
                    item
                    alignItems="center"
                    direction="row"
                    xs={12}
                    sm={8}
                    wrap="nowrap"
                    style={{
                      borderRadius: '.5rem',
                      backgroundColor: '#F4F6F8',
                      padding: '.5rem',
                    }}
                  >
                    <IconInfoActive
                      item
                      size={16}
                      className={classes.icon}
                      fill="#00234B"
                    />
                    <Divider flexItem orientation="vertical" />
                    <Grid item container className={classes.loginPromptText}>
                      <Typography variant="body2">
                        Your credit card information is not being stored in our
                        system.
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
              <FormProvider {...form}>
                <form>
                  <Box mb={4}>
                    <PaymentInformation
                      lessonType={lessonType}
                      paymentRequired={paymentRequired}
                      setPaymentRequired={setPaymentRequired}
                    />
                  </Box>
                  <Grid item>
                    <Button
                      variant="outlined"
                      className={classes.bookingButton}
                      onClick={form.handleSubmit(onSubmit())}
                      data-testid="complete-booking-button"
                    >
                      Complete Purchase
                    </Button>
                  </Grid>
                </form>
              </FormProvider>
            </Grid>
          </Grid>
        </Grid>
      </ThemeProvider>
    </Container>
  )
}
LessonReviewLayout.propTypes = {
  lessonType: PropTypes.object.isRequired,
  locationId: PropTypes.string.isRequired,
  startDateTime: PropTypes.string.isRequired,
  student: PropTypes.object.isRequired,
  previousUrl: PropTypes.string,
  isComplete: PropTypes.bool,
  setIsComplete: PropTypes.func,
  setLastDigits: PropTypes.func,
  setCardBrand: PropTypes.func,
  coachIsViewingOwnSchedule: PropTypes.bool,
}

LessonReviewLayout.defaultProps = {
  isComplete: false,
  coachIsViewingOwnSchedule: false,
}

export default withStripeElements(LessonReviewLayout)
